import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Tooltip,
  Alert,
  Paper,
} from '@mui/material';
import TableToolbar from '../components/TableToolbar';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    plan_name: '',
    plan_amount: '',
    plan_type: '0',
    plan_duration: '0',
    num_of_workers: '',
  });

  // Pagination and filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    plan_type: '',
  });

  // Get token from localStorage
  const getAuthToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication token not found. Please login again.');
      // You might want to redirect to login page here
      return null;
    }
    return token;
  };

  // Common axios configuration with auth header
  const getAxiosConfig = () => {
    const token = getAuthToken();
    if (!token) return null;

    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  useEffect(() => {
    fetchPlans();
  }, [currentPage, filters]);

  const fetchPlans = async () => {
    const config = getAxiosConfig();
    if (!config) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...filters
      });
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/v1/plans?${params}`,
        config
      );
      
      if (response.data && response.data.data) {
        setPlans(response.data.data);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.total_pages || 1);
      } else {
        setPlans([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // You might want to redirect to login page here
      } else {
        toast.error(error.response?.data?.Message || 'Error fetching plans');
      }
      setPlans([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setSelectedPlan(plan);
      setFormData({
        plan_name: plan.plan_name,
        plan_amount: plan.amount,
        plan_type: plan.plan_type.toString(),
        plan_duration: plan.duration_type.toString(),
        num_of_workers: plan.num_of_workers,
      });
    } else {
      setSelectedPlan(null);
      setFormData({
        plan_name: '',
        plan_amount: '',
        plan_type: '0',
        plan_duration: '0',
        num_of_workers: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const config = getAxiosConfig();
    if (!config) return;

    const requestData = {
      plan_name: formData.plan_name || '',
      plan_amount: formData.plan_amount || '',
      plan_type: formData.plan_type || '0',
      plan_duration: formData.plan_duration || '0',
      num_of_workers: formData.num_of_workers || '',
    };

    try {
      let response;
      if (selectedPlan) {
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/v1/plans/${selectedPlan.id}`,
          requestData,
          config
        );
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/v1/plans`,
          requestData,
          config
        );
      }
      
      if (response.data.Status === 1) {
        toast.success(response.data.Message || (selectedPlan ? 'Plan updated successfully' : 'Plan created successfully'));
        handleCloseDialog();
        fetchPlans();
      } else {
        toast.error(response.data.Message || 'Error saving plan');
      }
    } catch (error) {
      console.error('API Error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // You might want to redirect to login page here
      } else {
        toast.error(error.response?.data?.Message || 'Error saving plan');
      }
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    const config = getAxiosConfig();
    if (!config) return;

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/v1/plans/${id}/status`,
        { status: currentStatus === 1 ? 0 : 1 },
        config
      );
      toast.success(`Plan ${currentStatus === 1 ? 'deactivated' : 'activated'} successfully`);
      fetchPlans();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // You might want to redirect to login page here
      } else {
        toast.error('Error updating plan status');
      }
    }
  };

  const handleDelete = async (id) => {
    const config = getAxiosConfig();
    if (!config) return;

    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_API_URL}/v1/plans/${id}`,
          config
        );
        
        if (response.data.Status === 1) {
          toast.success(response.data.Message || 'Plan deleted successfully');
        } else {
          toast.error(response.data.Message || 'Error deleting plan');
        }
        fetchPlans();
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error('Session expired. Please login again.');
          // You might want to redirect to login page here
        } else {
          toast.error(error.response?.data?.Message || 'Error deleting plan');
        }
      }
    }
  };

  const getPlanTypeText = (type) => {
    switch (type) {
      case 1: return 'Self';
      case 2: return 'Business';
      case 3: return 'Ace';
      default: return '-';
    }
  };

  const getDurationText = (type) => {
    switch (type) {
      case 1: return 'Monthly';
      case 2: return 'Yearly';
      case 3: return 'Free';
      default: return '-';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <TableToolbar title="Plan Management" />

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by name or type"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
                startAdornment={<FilterIcon />}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Plan Type</InputLabel>
              <Select
                name="plan_type"
                value={filters.plan_type}
                onChange={handleFilterChange}
                label="Plan Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Self</MenuItem>
                <MenuItem value="2">Business</MenuItem>
                <MenuItem value="3">Ace</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      <TableContainer component={Paper} sx={{ boxShadow: 2, width: '100%', overflowX: { xs: 'auto', sm: 'hidden' } }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white' }}>S.No</TableCell>
              <TableCell sx={{ color: 'white' }}>Plan Name</TableCell>
              <TableCell sx={{ color: 'white' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white' }}>Duration</TableCell>
              <TableCell sx={{ color: 'white' }}>Workers</TableCell>
              <TableCell sx={{ color: 'white' }}>Type</TableCell>
              <TableCell sx={{ color: 'white' }}>Status</TableCell>
              <TableCell sx={{ color: 'white' }}>Created At</TableCell>
              <TableCell sx={{ color: 'white' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Alert severity="info">No plans found</Alert>
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan, index) => (
                <TableRow key={plan.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{plan.plan_name}</TableCell>
                  <TableCell>${plan.amount}</TableCell>
                  <TableCell>{getDurationText(plan.duration_type)}</TableCell>
                  <TableCell>{plan.num_of_workers}</TableCell>
                  <TableCell>
                    <Chip
                      label={getPlanTypeText(plan.plan_type)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={plan.status === 1 ? <CheckCircleIcon /> : <CancelIcon />}
                      label={plan.status === 1 ? 'Active' : 'Inactive'}
                      color={plan.status === 1 ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(plan.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                     
                     
                      <Tooltip title={plan.status === 1 ? 'Deactivate' : 'Activate'}>
                        <Button
                          variant="outlined"
                          color={plan.status === 1 ? 'error' : 'success'}
                          size="small"
                          onClick={() => handleStatusChange(plan.id, plan.status)}
                        >
                          {plan.status === 1 ? 'Deactivate' : 'Activate'}
                        </Button>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {plans.length} of {totalItems} plans
        </Typography>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedPlan ? 'Edit Plan' : 'Add New Plan'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name"
                name="plan_name"
                value={formData.plan_name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                name="plan_amount"
                type="number"
                value={formData.plan_amount}
                onChange={handleInputChange}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Plan Type</InputLabel>
                <Select
                  name="plan_type"
                  value={formData.plan_type}
                  onChange={handleInputChange}
                  label="Plan Type"
                >
                  <MenuItem value="0">Select Plan Type</MenuItem>
                  <MenuItem value="1">Self</MenuItem>
                  <MenuItem value="2">Business</MenuItem>
                  <MenuItem value="3">Ace</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Duration</InputLabel>
                <Select
                  name="plan_duration"
                  value={formData.plan_duration}
                  onChange={handleInputChange}
                  label="Duration"
                >
                  <MenuItem value="0">Select Duration</MenuItem>
                  <MenuItem value="1">Monthly</MenuItem>
                  <MenuItem value="2">Yearly</MenuItem>
                  <MenuItem value="3">Free</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Number of Workers"
                name="num_of_workers"
                type="number"
                value={formData.num_of_workers}
                onChange={handleInputChange}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedPlan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Plans; 